import { useMemo, useReducer } from 'react';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import { UpdateCropMutation } from '../mutations';
import { mutateAndDisplayErrors } from '../utils/toast';

const initialCropState = {};

const cropReducer = (crops, action) => {
  switch (action.type) {
    case 'update': {
      const crop = crops[action.cropId];
      if (!crop) return crops;
      return {
        ...crops,
        [action.cropId]: { ...crop, ...action.cropState },
      };
    }
    case 'bulkAdd': {
      // Don't re-add duplicate crops
      const existingIds = Object.values(crops).map((s) => s.id);
      const filteredCrops = action.crops.filter(
        ({ id }) => !id || !existingIds.includes(id),
      );
      return {
        ...crops,
        ..._.keyBy(filteredCrops, 'id'),
      };
    }
    case 'reset': {
      return initialCropState;
    }
    default:
      throw new Error(`Invalid type: ${action.type}.`);
  }
};

export default () => {
  // STATE
  const [crops, dispatch] = useReducer(cropReducer, initialCropState);

  // MUTATIONS
  const [updateCropMutation] = useMutation(UpdateCropMutation);

  // UPDATES
  const updateCrop = (cropId, cropState) => {
    dispatch({ type: 'update', cropId, cropState });
  };

  const updateCropColor = async (crop, color) => {
    const { id, color: existingColor } = crop;

    // Update local state first, for optimistic rendering
    updateCrop(id, { color });

    // Make backend update
    await mutateAndDisplayErrors(
      () => updateCropMutation({ variables: { id, colorId: color.id } }),
      `Failed to update color for crop ${crop.name}`,
    )
      .then(({ updatedAt }) => {
        // Update timestamp
        updateCrop(id, { updatedAt });
      })
      .catch(() => {
        // Revert local state
        updateCrop(id, { color: existingColor });
      });
  };

  const addCrops = (newCrops, { upload = false } = {}) => {
    if (upload) {
      throw new Error(`Please implement bulkAdd upload for crops`);
    }
    dispatch({ type: 'bulkAdd', crops: newCrops });
  };

  const resetCrops = () => {
    dispatch({ type: 'reset' });
  };

  // Produce a mapping from crop.name -> crop.id so that ids stored
  // in crop overrids and features can be remapped
  const cropNameToId = useMemo(() => _.invert(_.mapValues(crops, 'name')), [
    crops,
  ]);

  const cropVarietyIdToCropId = useMemo(
    () =>
      Object.values(crops).reduce(
        (acc, crop) => ({
          ...acc,
          ...crop.cropVarieties?.reduce(
            (cvMap, cropVariety) => ({ ...cvMap, [cropVariety.id]: crop.id }),
            {},
          ),
        }),
        {},
      ),
    [crops],
  );

  return {
    cropNameToId,
    cropVarietyIdToCropId,
    availableCrops: crops,
    updateCrop, // Update the crop (local only)
    updateCropColor, // Update the crop color
    addCrops, // add crops (local only)
    resetCrops, // clear the crop state
  };
};
